// =============================================================================
// Leitura de planilha colada ou em CSV
//
// O Financeiro trabalha em planilha. Colar do Excel produz TSV; salvar como CSV
// no Brasil produz ponto-e-vírgula, porque a vírgula já é o separador decimal.
// Aceitar os três (tab, ponto-e-vírgula, vírgula) evita a pergunta "salva como
// o quê?" — que é onde esse tipo de tela costuma morrer.
// =============================================================================

export type Coluna = "centro_custo" | "categoria" | "tipo" | "valor" | "competencia" | "subcategoria";

/** Nomes que o cabeçalho pode ter. Comparação sem acento e sem caixa. */
const SINONIMOS: Record<Coluna, string[]> = {
  centro_custo: ["centro de custo", "centro custo", "cc", "codigo cc", "centro_custo"],
  categoria: ["categoria", "categoria financeira", "conta", "plano de contas"],
  tipo: ["tipo", "tipo de orcamento"],
  valor: ["valor", "valor orcado", "orcado", "teto", "valor total"],
  competencia: ["competencia", "mes", "mes de competencia", "referencia"],
  // Opcional (18/09). "Histórico" é como a aba MOVIMENTACOES da planilha do
  // Financeiro chama a mesma coisa — e é por esse nome que o motor casa o
  // lançamento pela descrição (§3.5).
  subcategoria: ["subcategoria", "sub categoria", "sub-categoria", "historico", "descricao da despesa"],
};

/** Colunas que podem faltar: sem elas, a RPC usa o default (pontual, sem subcategoria). */
const OPCIONAIS: Coluna[] = ["tipo", "subcategoria"];

/**
 * Divide uma linha respeitando aspas.
 *
 * ⚠️ Achado da revisão de 18/09: o `split` simples ignorava aspas, e o CSV do
 * Google Sheets — que põe entre aspas todo campo com vírgula — perdia os
 * centavos: `...;"1234,56"` com a vírgula como separador virava "1234" e
 * "56" em colunas diferentes, e o valor entrava como R$ 1.234,00 sem erro.
 * Aspas duplas dentro de campo entre aspas (`""`) viram uma aspa.
 */
export function dividir(linha: string, separador: string): string[] {
  const campos: string[] = [];
  let atual = "";
  let entreAspas = false;
  for (let i = 0; i < linha.length; i++) {
    const ch = linha[i]!;
    if (entreAspas) {
      if (ch === '"' && linha[i + 1] === '"') { atual += '"'; i++; }
      else if (ch === '"') entreAspas = false;
      else atual += ch;
    } else if (ch === '"' && atual.trim() === "") {
      entreAspas = true;
      atual = "";
    } else if (ch === separador) {
      campos.push(atual);
      atual = "";
    } else {
      atual += ch;
    }
  }
  campos.push(atual);
  return campos;
}

function normalizar(s: string): string {
  return s
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

/** Escolhe o separador pelo que aparece mais na primeira linha. */
export function detectarSeparador(primeira: string): string {
  const candidatos = ["\t", ";", ","];
  let melhor = "\t";
  let maior = -1;
  for (const c of candidatos) {
    const n = primeira.split(c).length;
    if (n > maior) { maior = n; melhor = c; }
  }
  return melhor;
}

export type LinhaPlanilha = Record<Coluna, string>;

export type Leitura = {
  linhas: LinhaPlanilha[];
  colunasEncontradas: Coluna[];
  colunasFaltando: Coluna[];
  separador: string;
  erro?: string;
};

export function lerPlanilha(texto: string): Leitura {
  const cruas = texto.replace(/\r\n?/g, "\n").split("\n").filter((l) => l.trim() !== "");
  const vazio: Leitura = {
    linhas: [],
    colunasEncontradas: [],
    colunasFaltando: Object.keys(SINONIMOS) as Coluna[],
    separador: "\t",
  };

  if (cruas.length < 2) {
    return { ...vazio, erro: "Cole ao menos o cabeçalho e uma linha de dados." };
  }

  const separador = detectarSeparador(cruas[0]!);
  const cabecalho = dividir(cruas[0]!, separador).map(normalizar);

  // Cada coluna nossa procura sua posição no cabeçalho colado. Exigir ordem
  // fixa quebraria com qualquer planilha real, que sempre tem coluna a mais.
  const posicao = {} as Record<Coluna, number>;
  for (const [coluna, nomes] of Object.entries(SINONIMOS) as [Coluna, string[]][]) {
    const i = cabecalho.findIndex((c) => nomes.includes(c));
    if (i >= 0) posicao[coluna] = i;
  }

  const encontradas = Object.keys(posicao) as Coluna[];
  const faltando = (Object.keys(SINONIMOS) as Coluna[]).filter((c) => !encontradas.includes(c));

  // `tipo` e `subcategoria` são opcionais: sem elas, tudo entra como pontual
  // e sem subcategoria, que é o default da própria RPC.
  const faltandoObrigatorias = faltando.filter((c) => !OPCIONAIS.includes(c));
  if (faltandoObrigatorias.length > 0) {
    return {
      linhas: [],
      colunasEncontradas: encontradas,
      colunasFaltando: faltando,
      separador,
      erro: `Não achei no cabeçalho: ${faltandoObrigatorias.join(", ")}.`,
    };
  }

  const linhas = cruas.slice(1).map((crua) => {
    const celulas = dividir(crua, separador);
    const linha = {} as LinhaPlanilha;
    for (const coluna of Object.keys(SINONIMOS) as Coluna[]) {
      const i = posicao[coluna];
      linha[coluna] = i === undefined ? "" : (celulas[i] ?? "").trim();
    }
    return linha;
  });

  return { linhas, colunasEncontradas: encontradas, colunasFaltando: faltando, separador };
}

/**
 * Linhas de exemplo do modelo — as mesmas do placeholder da tela, para que o
 * que se vê ao colar e o que se baixa nunca digam coisas diferentes. Valores
 * sintéticos; CC e categorias são do plano de contas real, então o modelo
 * importado sem edição passa na conferência.
 */
function linhasModelo(ano: number): string[][] {
  return [
    ["Centro de custo", "Categoria", "Tipo", "Valor", "Competência", "Subcategoria"],
    ["300", "Tráfego Pago (Ads - Facebook/Google)", "recorrente", "20.000,00", `01/${ano}`, ""],
    ["200", "Endomarketing", "pontual", "500,00", `04/${ano}`, "Ovo de Páscoa"],
    ["200", "Endomarketing", "pontual", "300,00", `04/${ano}`, "Presente de aniversário"],
  ];
}

/** O exemplo no formato de colar do Excel (tab), para o placeholder. */
export function exemploColado(ano: number): string {
  return linhasModelo(ano).map((l) => l.join("\t")).join("\n");
}

/**
 * O modelo para baixar. Ponto-e-vírgula porque é o que o Excel em português
 * abre já separado em colunas — com vírgula, abriria tudo numa coluna só, já
 * que a vírgula é o decimal. O BOM fica a cargo de quem grava o arquivo.
 */
export function modeloCsv(ano: number): string {
  return linhasModelo(ano).map((l) => l.join(";")).join("\r\n") + "\r\n";
}

/**
 * Bytes do arquivo escolhido → texto.
 *
 * ⚠️ O caminho natural de quem baixa o modelo é abrir no Excel, preencher e
 * salvar como "CSV (separado por vírgulas)" — e o Excel grava isso em
 * Windows-1252, não UTF-8. Lido como UTF-8, "Competência" vira "Compet�ncia",
 * o cabeçalho não casa e a planilha inteira é recusada; pior, "Tráfego Pago"
 * vira uma categoria que não existe. Então: UTF-8 estrito primeiro, e só se
 * ele recusar os bytes, Windows-1252.
 */
export function decodificarArquivo(bytes: ArrayBuffer | Uint8Array): string {
  try {
    return new TextDecoder("utf-8", { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder("windows-1252").decode(bytes);
  }
}

/**
 * "09/2027", "2027-09", "set/27", "1/9/2027" → "2027-09-01".
 *
 * Aceita o que a planilha realmente contém. Devolve null quando não dá para ler
 * — nunca chuta, porque competência errada aloca o gasto no mês errado e isso
 * só aparece no fechamento.
 */
export function lerCompetencia(bruto: string, anoPadrao?: number): string | null {
  const s = bruto.trim();
  if (!s) return null;

  let m = /^(\d{4})[-/](\d{1,2})(?:[-/]\d{1,2})?$/.exec(s);
  if (m) return `${m[1]}-${m[2]!.padStart(2, "0")}-01`;

  m = /^(\d{1,2})[-/](\d{4})$/.exec(s);
  if (m) return `${m[2]}-${m[1]!.padStart(2, "0")}-01`;

  m = /^(\d{1,2})[-/](\d{1,2})[-/](\d{2,4})$/.exec(s);
  if (m) {
    const ano = m[3]!.length === 2 ? `20${m[3]}` : m[3];
    return `${ano}-${m[2]!.padStart(2, "0")}-01`;
  }

  const MESES = ["jan","fev","mar","abr","mai","jun","jul","ago","set","out","nov","dez"];
  m = /^([a-zç]{3,})[-/ ]?(\d{2,4})?$/i.exec(normalizar(s));
  if (m) {
    const i = MESES.indexOf(m[1]!.slice(0, 3));
    if (i >= 0) {
      const anoBruto = m[2];
      const ano = anoBruto
        ? anoBruto.length === 2 ? `20${anoBruto}` : anoBruto
        : anoPadrao ? String(anoPadrao) : null;
      if (ano) return `${ano}-${String(i + 1).padStart(2, "0")}-01`;
    }
  }

  return null;
}
